import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiArrowDown, FiArrowUp, FiEdit2, FiFileText, FiFilter, FiTrash2 } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/layout/Sidebar';
import { Button, Card, Input, Modal, SearchableSelect, Table, confirmDialog, toast } from '../../components/ui';
import { useAuth, usePreferences } from '../../contexts';
import { api, ApiNetworkError } from '../../services/api';
import styles from './Lancamentos.module.css';
import { ORDENACAO_PADRAO, ehColunaOrdenavel, proximaOrdenacao, type Ordenacao } from './ordenacao';

type LancamentoCategoria = 'GASTOS' | 'RECEITA';

interface Lancamento {
    id: string;
    categoria: LancamentoCategoria;
    data: string;
    mes: number;
    ano: number;
    subCategoria: string;
    formaPagamento: string;
    valor: number;
    informacoesAdicionais?: string;
    filialId?: string;
    filialNome?: string;
    /** Quem fez o lancamento (ou o dono da empresa, quando nao se sabe). */
    lancadoPor?: string | null;
    createdAt: string;
    updatedAt: string;
}

interface LancamentoFormData {
    categoria: LancamentoCategoria;
    data: string;
    subCategoria: string;
    formaPagamento: string;
    valor: number;
    informacoesAdicionais: string;
}

interface Summary {
    totalReceita: number;
    totalGastos: number;
    resultado: number;
    totalLancamentos: number;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

const initialFormData: LancamentoFormData = {
    categoria: 'GASTOS',
    data: '',
    subCategoria: '',
    formaPagamento: '',
    valor: 0,
    informacoesAdicionais: '',
};

export default function Lancamentos() {
    // Negocios da empresa ativa: o filtro nunca mostra os de outra empresa.
    const { filialAtiva, hasFeature, filiais } = useAuth();
    const [filialFilter, setFilialFilter] = useState<string[]>([]);

    // O filtro por negócio só faz sentido na visão consolidada: quando o header
    // está fixado em um negócio, o backend já restringe a consulta àquele negócio.
    // Sem a feature de filiais nao ha o que separar por negocio: some a coluna,
    // o filtro e as colunas dos exports.
    const isAll = hasFeature('filiais') && filialAtiva?.id === 'all' && filiais.length > 1;
    const filialMap = useMemo(
        () => Object.fromEntries(filiais.map(f => [f.id, f.name])) as Record<string, string>,
        [filiais],
    );

    const [lancamentos, setLancamentos] = useState<Lancamento[]>([]);
    const [summary, setSummary] = useState<Summary>({ totalReceita: 0, totalGastos: 0, resultado: 0, totalLancamentos: 0 });
    const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [search, setSearch] = useState('');
    const [categoriaFilter, setCategoriaFilter] = useState<LancamentoCategoria | 'all'>('all');
    const [mesFilter, setMesFilter] = useState<number | ''>('');
    const [anoFilter, setAnoFilter] = useState<number | ''>('');

    // Periodo pela data do lancamento (MAN-133).
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');

    // Sempre pela data do lancamento; o cliente reordena clicando no cabecalho.
    // A escolha "por cadastro / por data" que ficava salva no navegador saiu.
    const [ordenacao, setOrdenacao] = useState<Ordenacao>(ORDENACAO_PADRAO);
    useEffect(() => {
        localStorage.removeItem('lancamentos_orderBy');
    }, []);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLancamento, setEditingLancamento] = useState<Lancamento | null>(null);
    const [formData, setFormData] = useState<LancamentoFormData>(initialFormData);
    const [obsModal, setObsModal] = useState<string | null>(null);

    const [subcategorias, setSubcategorias] = useState<string[]>([]);
    const [formasPagamento, setFormasPagamento] = useState<string[]>([]);
    const [categoriasRaw, setCategoriasRaw] = useState<any[]>([]);
    const [categoriaLabelFilter, setCategoriaLabelFilter] = useState('');
    const [subCategoriaLabelFilter, setSubCategoriaLabelFilter] = useState('');
    const { formatCurrency, t, currencySymbol, prefs } = usePreferences();
    const { hasPermission } = useAuth();
    const canEdit = hasPermission('LANCAMENTOS', 'EDIT');
    const canDelete = hasPermission('LANCAMENTOS', 'DELETE');
    const categoriaLabels: Record<LancamentoCategoria, string> = {
        GASTOS: t('lanc.gasto'),
        RECEITA: t('lanc.receita'),
    };

    const [subCategoriasFiltradas, setSubCategoriasFiltradas] = useState<string[]>([]);
    const [anos, setAnos] = useState<number[]>([]);
    const [subCategoriasFull, setSubCategoriasFull] = useState<any[]>([]);

    // Filtros e ordem da consulta: os mesmos na tela, nos cards e nos exports.
    const filtrosDaConsulta = useCallback((): Record<string, any> => {
        const params: Record<string, any> = { orderBy: ordenacao.campo, direcao: ordenacao.direcao };
        if (categoriaFilter !== 'all') params.categoria = categoriaFilter;
        if (mesFilter) params.mes = mesFilter;
        if (anoFilter) params.ano = anoFilter;
        if (search) params.subCategoria = search;
        if (categoriaLabelFilter) params.categoriaLabel = categoriaLabelFilter;
        if (subCategoriaLabelFilter) params.subCategoriaLabel = subCategoriaLabelFilter;
        if (dataInicio) params.dataInicio = dataInicio;
        if (dataFim) params.dataFim = dataFim;
        if (filialFilter.length > 0) params.filialIds = filialFilter.join(',');
        return params;
    }, [categoriaFilter, mesFilter, anoFilter, search, categoriaLabelFilter, subCategoriaLabelFilter, dataInicio, dataFim, ordenacao, filialFilter]);

    const loadData = useCallback(async (page = 1) => {
        try {
            setIsLoading(true);
            const params: Record<string, any> = { ...filtrosDaConsulta(), page, limit: 20 };

            const [lancamentosResult, summaryResult, subcategoriasResult, formasPagamentoResult, planoCategoriaResult, anosResult, subCategoriaFullResult] = await Promise.all([
                api.getLancamentos(params),
                api.getLancamentosSummary(params),
                api.getLancamentosSubcategorias(),
                api.getLancamentosFormasPagamento(),
                api.getCategorias(),
                api.getLancamentosAnos(),
                api.getSubCategorias(),
            ]);

            setLancamentos(lancamentosResult.data);
            setPagination(lancamentosResult.pagination);
            setSummary({ totalReceita: summaryResult.totalReceita, totalGastos: summaryResult.totalGastos, resultado: summaryResult.resultado, totalLancamentos: summaryResult.totalLancamentos });
            setSubcategorias(subcategoriasResult);
            setFormasPagamento(formasPagamentoResult);
            setCategoriasRaw(planoCategoriaResult);
            setAnos(anosResult);
            setSubCategoriasFull(subCategoriaFullResult);
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            const msg = error instanceof ApiNetworkError ? error.friendlyMessage : t('lanc.erroCarregar');
            toast(msg, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [filtrosDaConsulta]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        setFilialFilter([]);
    }, [filialAtiva?.id]);

    const formatDate = (date: string) => {
        const [y, m, d] = date.split('T')[0].split('-');
        return `${d}/${m}/${y}`;
    };

    const formatInputCurrency = (value: string) => {
        const numericValue = value.replace(/\D/g, '');
        if (!numericValue) return '';
        const numValue = parseFloat(numericValue) / 100;
        return numValue.toLocaleString(prefs.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const parseInputToNumber = (formattedValue: string): number => {
        if (!formattedValue) return 0;
        if (prefs.language === 'en-US') return parseFloat(formattedValue.replace(/,/g, '')) || 0;
        return parseFloat(formattedValue.replace(/\./g, '').replace(',', '.')) || 0;
    };

    const fetchAllForExport = async (): Promise<Lancamento[]> => {
        const params: Record<string, any> = { ...filtrosDaConsulta(), page: 1, limit: pagination.total || 10000 };
        const result = await api.getLancamentos(params);
        return result.data;
    };

    const handleExportExcel = async () => {
        const allLancamentos = await fetchAllForExport();
        const rows = allLancamentos.map((l) => ({
            Data: formatDate(l.data),
            Categoria: categoriaLabels[l.categoria],
            Subcategoria: t(l.subCategoria),
            'Forma de Pagamento': t(l.formaPagamento),
            Valor: Number(l.valor),
            ...(isAll ? { 'Negócio': l.filialId ? filialMap[l.filialId] || '-' : '-' } : {}),
            Observações: l.informacoesAdicionais || '',
            'Lançado por': l.lancadoPor || '-',
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Lançamentos');

        worksheet['!cols'] = [
            { wch: 12 },
            { wch: 12 },
            { wch: 22 },
            { wch: 22 },
            { wch: 14 },
            ...(isAll ? [{ wch: 20 }] : []),
            { wch: 30 },
            { wch: 24 },
        ];

        const fileName = `lancamentos_${anoFilter || 'todos'}${mesFilter ? `_${String(mesFilter).padStart(2, '0')}` : ''}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const handleExportPDF = async () => {
        const allLancamentos = await fetchAllForExport();
        const doc = new jsPDF({ orientation: 'landscape' });
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        doc.setFillColor(1, 95, 89);
        doc.rect(0, 0, pageWidth, 32, 'F');
        doc.setFontSize(16);
        doc.setTextColor(255, 255, 255);
        doc.text('Relatório de Lançamentos', 14, 14);

        const mesSelecionado = meses.find(m => m.value === mesFilter);
        const periodoLabel = dataInicio || dataFim
            ? `${dataInicio ? formatDate(dataInicio) : '...'} a ${dataFim ? formatDate(dataFim) : '...'}`
            : mesSelecionado
                ? `${mesSelecionado.label}${anoFilter ? ` / ${anoFilter}` : ''}`
                : anoFilter ? `Ano ${anoFilter}` : 'Todo o período';
        doc.setFontSize(10);
        doc.text(`Período: ${periodoLabel}`, 14, 22);

        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.text(`Receitas: ${formatCurrency(summary.totalReceita)}   Gastos: ${formatCurrency(summary.totalGastos)}   Resultado: ${formatCurrency(summary.resultado)}`, 14, 29);

        doc.setTextColor(0, 0, 0);
        const pdfHeaders = ['Data', 'Categoria', 'Subcategoria', 'Forma Pagamento', 'Valor', ...(isAll ? ['Negócio'] : []), 'Observações', 'Lançado por'];
        const obsIndex = isAll ? 6 : 5;

        const colStyles: Record<number, { cellWidth: number; overflow: 'linebreak' }> = {
            0: { cellWidth: 22, overflow: 'linebreak' },
            1: { cellWidth: 22, overflow: 'linebreak' },
            2: { cellWidth: isAll ? 42 : 50, overflow: 'linebreak' },
            3: { cellWidth: isAll ? 32 : 37, overflow: 'linebreak' },
            4: { cellWidth: 27, overflow: 'linebreak' },
        };
        if (isAll) colStyles[5] = { cellWidth: 24, overflow: 'linebreak' };
        // Largura util da pagina (A4 deitada, margens de 14): 269mm.
        colStyles[obsIndex] = { cellWidth: isAll ? 70 : 81, overflow: 'linebreak' };
        colStyles[obsIndex + 1] = { cellWidth: 30, overflow: 'linebreak' };

        autoTable(doc, {
            startY: 38,
            head: [pdfHeaders],
            body: allLancamentos.map((l) => {
                const obs = l.informacoesAdicionais || '';
                return [
                    formatDate(l.data),
                    categoriaLabels[l.categoria],
                    l.subCategoria,
                    l.formaPagamento,
                    formatCurrency(Number(l.valor)),
                    ...(isAll ? [l.filialId ? filialMap[l.filialId] || '-' : '-'] : []),
                    obs.length > 120 ? obs.slice(0, 120) + '…' : obs,
                    l.lancadoPor || '-',
                ];
            }),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [1, 95, 89], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [236, 253, 245] },
            columnStyles: colStyles,
        });

        const pageCount = (doc as any).internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFillColor(1, 95, 89);
            doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
            doc.setFontSize(8);
            doc.setTextColor(255, 255, 255);
            doc.text('Financeiro Diário - Sistema de Gestão Financeira', 14, pageHeight - 6);
            doc.text(`Página ${i} de ${pageCount}`, pageWidth - 32, pageHeight - 6);
        }

        const fileName = `lancamentos_${anoFilter || 'todos'}${mesFilter ? `_${String(mesFilter).padStart(2, '0')}` : ''}.pdf`;
        doc.save(fileName);
    };

    const openEditModal = (lancamento: Lancamento) => {
        setEditingLancamento(lancamento);
        setFormData({
            categoria: lancamento.categoria,
            data: lancamento.data.split('T')[0],
            subCategoria: lancamento.subCategoria,
            formaPagamento: lancamento.formaPagamento,
            valor: Number(lancamento.valor),
            informacoesAdicionais: lancamento.informacoesAdicionais || '',
        });
        setIsModalOpen(true);
    };

    const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatInputCurrency(e.target.value);
        const numericValue = parseInputToNumber(formatted);
        setFormData(prev => ({ ...prev, valor: numericValue }));
    };

    const handleInputChange = (field: keyof LancamentoFormData, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            const lancamentoData = {
                categoria: formData.categoria,
                data: formData.data,
                subCategoria: formData.subCategoria,
                formaPagamento: formData.formaPagamento,
                valor: formData.valor,
                informacoesAdicionais: formData.informacoesAdicionais || null,
            };

            if (editingLancamento) {
                await api.updateLancamento(editingLancamento.id, lancamentoData);
            } else {
                await api.createLancamento(lancamentoData);
            }
            await loadData(pagination.page);
            setIsModalOpen(false);
        } catch (error) {
            console.error('Erro ao salvar lançamento:', error);
            const msg = error instanceof Error ? error.message : 'Erro ao salvar lançamento.';
            toast(msg, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (deletingId) return;
        setDeletingId(id);
        try {
            const confirmed = await confirmDialog(t('lanc.confirmarExcluir'));
            if (confirmed) {
                try {
                    await api.deleteLancamento(id);
                    await loadData(pagination.page);
                } catch (error) {
                    console.error('Erro ao excluir lançamento:', error);
                    toast(t('lanc.erroExcluir'), 'error');
                }
            }
        } finally {
            setDeletingId(null);
        }
    };

    const handlePageChange = (newPage: number) => {
        loadData(newPage);
    };

    const handleSort = (coluna: string) => {
        if (ehColunaOrdenavel(coluna)) setOrdenacao((atual) => proximaOrdenacao(atual, coluna));
    };

    const handleClearFilters = () => {
        setSearch('');
        setDataInicio('');
        setDataFim('');
        setCategoriaFilter('all');
        setCategoriaLabelFilter('');
        setSubCategoriaLabelFilter('');
        setSubCategoriasFiltradas([]);
        setAnoFilter('');
        setMesFilter('');
        setFilialFilter([]);
    };

    const hasActiveFilters = search !== '' || dataInicio !== '' || dataFim !== '' || categoriaFilter !== 'all' || categoriaLabelFilter !== '' || subCategoriaLabelFilter !== '' || anoFilter !== '' || mesFilter !== '' || filialFilter.length > 0;

    const meses = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Intl.DateTimeFormat(prefs.language, { month: 'long' }).format(new Date(2000, i, 1)),
    })), [prefs.language]);

    // MAN-133: data primeiro, forma de pagamento na propria coluna e as colunas
    // de dado ordenaveis pelo cabecalho.
    const columns = [
        {
            key: 'data',
            header: t('lanc.data'),
            sortable: true,
            render: (lancamento: Lancamento) => (
                <span className={styles.data}>{formatDate(lancamento.data)}</span>
            ),
        },
        {
            key: 'categoria',
            header: t('lanc.tipoLancamento'),
            sortable: true,
            render: (lancamento: Lancamento) => (
                <div className={styles.categoria}>
                    <span className={`${styles.categoriaIcon} ${styles[lancamento.categoria.toLowerCase()]}`}>
                        {lancamento.categoria === 'RECEITA' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />}
                    </span>
                    <span className={`${styles.badge} ${lancamento.categoria === 'RECEITA' ? styles.badgeReceita : styles.badgeGastos}`}>
                        {categoriaLabels[lancamento.categoria]}
                    </span>
                </div>
            ),
        },
        {
            key: 'subCategoria',
            header: t('lanc.categoria'),
            sortable: true,
            render: (lancamento: Lancamento) => (
                <span className={styles.subCategoria}>{t(lancamento.subCategoria)}</span>
            ),
        },
        {
            key: 'formaPagamento',
            header: t('receita.formaPagamento'),
            sortable: true,
            render: (lancamento: Lancamento) => (
                <span className={styles.formaPagamento}>{t(lancamento.formaPagamento)}</span>
            ),
        },
        {
            key: 'valor',
            header: t('lanc.valor'),
            sortable: true,
            render: (lancamento: Lancamento) => (
                <span className={`${styles.valor} ${lancamento.categoria === 'RECEITA' ? styles.valorReceita : styles.valorGastos}`}>
                    {lancamento.categoria === 'RECEITA' ? '+' : '-'}{formatCurrency(Number(lancamento.valor))}
                </span>
            ),
        },
        ...(isAll ? [{
            key: 'filial',
            header: t('lanc.filial'),
            render: (lancamento: Lancamento) => {
                const nome = lancamento.filialNome || (lancamento.filialId ? filialMap[lancamento.filialId] : null) || '-';
                return (
                    <span className={styles.filialName} title={nome}>{nome}</span>
                );
            },
        }] : []),
        {
            key: 'informacoesAdicionais',
            header: t('lanc.descricao'),
            render: (lancamento: Lancamento) => {
                const obs = lancamento.informacoesAdicionais || '';
                if (!obs) return <span className={styles.infoAdicional}>-</span>;
                return (
                    <span className={styles.infoAdicional}>
                        {obs.length > 60 ? obs.slice(0, 60) + '… ' : obs}
                        {obs.length > 60 && (
                            <button
                                className={styles.verMaisBtn}
                                onClick={() => setObsModal(obs)}
                            >
                                {t('common.verMais')}
                            </button>
                        )}
                    </span>
                );
            },
        },
        {
            key: 'lancadoPor',
            header: t('lanc.lancadoPor'),
            render: (lancamento: Lancamento) => (
                <span className={styles.lancadoPor} title={lancamento.lancadoPor || undefined}>
                    {lancamento.lancadoPor || '-'}
                </span>
            ),
        },
        {
            key: 'actions',
            header: t('common.acoes'),
            render: (lancamento: Lancamento) => (
                <div className={styles.actionButtons}>
                    {canEdit && (
                        <button
                            className={styles.actionBtn}
                            onClick={() => openEditModal(lancamento)}
                            title="Editar"
                        >
                            <FiEdit2 size={16} />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => handleDelete(lancamento.id)}
                            disabled={deletingId !== null}
                            title="Excluir"
                        >
                            <FiTrash2 size={16} />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header title={t('lanc.titulo')} subtitle={t('lanc.subtitulo')} />
                <div className={styles.content}>
                    {/* MAN-133: os totais ficam acima dos filtros; receita em verde e
                        resultado verde ou vermelho conforme o sinal. */}
                    <div className={styles.summaryCards}>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>{t('lanc.totalReceitas')}</div>
                            <div className={`${styles.summaryValue} ${styles.receita}`}>
                                {formatCurrency(summary.totalReceita)}
                            </div>
                        </div>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>{t('lanc.totalGastos')}</div>
                            <div className={`${styles.summaryValue} ${styles.gastos}`}>
                                {formatCurrency(summary.totalGastos)}
                            </div>
                        </div>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>{t('lanc.resultado')}</div>
                            <div className={`${styles.summaryValue} ${summary.resultado < 0 ? styles.gastos : styles.receita}`}>
                                {formatCurrency(summary.resultado)}
                            </div>
                        </div>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>{t('lanc.lancamentos')}</div>
                            <div className={styles.summaryValue}>{summary.totalLancamentos}</div>
                        </div>
                    </div>

                    <Card className={styles.filtersCard}>
                        <div className={styles.filtersHeader}>
                            <FiFilter size={18} />
                            <span>Filtros</span>
                        </div>
                        <div className={styles.filtersBody}>
                            <div className={`${styles.filterGroup} ${styles.filterGroupData}`}>
                                <label htmlFor="lanc-data-de">{t('lanc.dataDe')}</label>
                                <input
                                    id="lanc-data-de"
                                    type="date"
                                    className={styles.dateInput}
                                    value={dataInicio}
                                    max={dataFim || undefined}
                                    onChange={(e) => setDataInicio(e.target.value)}
                                />
                            </div>
                            <div className={`${styles.filterGroup} ${styles.filterGroupData}`}>
                                <label htmlFor="lanc-data-ate">{t('lanc.dataAte')}</label>
                                <input
                                    id="lanc-data-ate"
                                    type="date"
                                    className={styles.dateInput}
                                    value={dataFim}
                                    min={dataInicio || undefined}
                                    onChange={(e) => setDataFim(e.target.value)}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('lanc.tipo')}</label>
                                <SearchableSelect
                                    value={categoriaFilter}
                                    onChange={(v) => setCategoriaFilter(v as LancamentoCategoria | 'all')}
                                    options={[
                                        { value: 'all', label: t('lanc.todos') },
                                        { value: 'RECEITA', label: t('lanc.receitas') },
                                        { value: 'GASTOS', label: t('lanc.despesas') },
                                    ]}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('lanc.grupo')}</label>
                                <SearchableSelect
                                    value={categoriaLabelFilter}
                                    onChange={async (label) => {
                                        setCategoriaLabelFilter(label);
                                        setSubCategoriaLabelFilter('');
                                        if (!label) { setSubCategoriasFiltradas([]); return; }
                                        const cat = categoriasRaw.find((c: any) => c.nome === label);
                                        if (!cat) return;
                                        const subs = await api.getSubCategorias({ categoriaId: cat.id });
                                        setSubCategoriasFiltradas(subs.map((s: any) => s.nome));
                                    }}
                                    emptyLabel={t('lanc.todosGrupos')}
                                    options={categoriasRaw.map((cat: any) => ({
                                        value: cat.nome,
                                        label: t(cat.nome),
                                        prefix: (
                                            <span className={`${styles.tipoBadge} ${cat.tipo === 'ENTRADA' ? styles.tipoBadgeReceita : styles.tipoBadgeGastos}`}>
                                                {cat.tipo === 'ENTRADA' ? 'RECEITA' : 'DESPESA'}
                                            </span>
                                        ),
                                    }))}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('lanc.categoria')}</label>
                                <SearchableSelect
                                    value={subCategoriaLabelFilter}
                                    onChange={setSubCategoriaLabelFilter}
                                    disabled={!categoriaLabelFilter}
                                    emptyLabel={t('lanc.todasCategorias')}
                                    options={subCategoriasFiltradas.map((s) => ({ value: s, label: t(s) }))}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('common.ano')}</label>
                                <SearchableSelect
                                    value={String(anoFilter)}
                                    onChange={(v) => setAnoFilter(v ? Number(v) : '')}
                                    emptyLabel={t('lanc.todosAnos')}
                                    options={anos.map(a => ({ value: String(a), label: String(a) }))}
                                />
                            </div>
                            <div className={styles.filterGroup}>
                                <label>{t('rel.mes')}</label>
                                <SearchableSelect
                                    value={String(mesFilter)}
                                    onChange={(v) => setMesFilter(v ? Number(v) : '')}
                                    emptyLabel={t('lanc.todosMeses')}
                                    options={meses.map(m => ({ value: String(m.value), label: m.label }))}
                                />
                            </div>
                        </div>
                        <div className={styles.searchRow}>
                            <Input
                                placeholder={t('lanc.buscarCategoria')}
                                aria-label={t('lanc.buscarCategoria')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        {isAll && (
                            <div className={styles.filialFilterRow}>
                                <span className={styles.filialFilterLabel}>{t('rel.filiais')}</span>
                                <div className={styles.filialChips}>
                                    {filiais.map(f => (
                                        <label
                                            key={f.id}
                                            className={`${styles.filialChip} ${filialFilter.includes(f.id) ? styles.filialChipActive : ''}`}
                                            title={f.name}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={filialFilter.includes(f.id)}
                                                onChange={(e) => {
                                                    setFilialFilter(prev =>
                                                        e.target.checked
                                                            ? [...prev, f.id]
                                                            : prev.filter(id => id !== f.id)
                                                    );
                                                }}
                                            />
                                            <span>{f.name}</span>
                                        </label>
                                    ))}
                                </div>
                                {filialFilter.length > 0 && (
                                    <button
                                        type="button"
                                        className={styles.clearFilter}
                                        onClick={() => setFilialFilter([])}
                                    >
                                        {t('rel.limpar', { n: String(filialFilter.length) })}
                                    </button>
                                )}
                            </div>
                        )}
                        <div className={`${styles.filtersFooter} ${styles.filtersFooterFim}`}>
                            <div className={styles.filterActions}>
                                {hasActiveFilters && (
                                    <button
                                        className={styles.clearFiltersBtn}
                                        onClick={handleClearFilters}
                                    >
                                        {t('common.limpar')}
                                    </button>
                                )}
                                <button
                                    className={styles.exportBtn}
                                    onClick={handleExportExcel}
                                    title="Exportar Excel"
                                    disabled={lancamentos.length === 0}
                                >
                                    <FiFileText size={15} />
                                    Excel
                                </button>
                                <button
                                    className={`${styles.exportBtn} ${styles.exportPdfBtn}`}
                                    onClick={handleExportPDF}
                                    title="Exportar PDF"
                                    disabled={lancamentos.length === 0}
                                >
                                    <FiFileText size={15} />
                                    PDF
                                </button>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className={styles.table}>
                            {isLoading ? (
                                <div style={{ textAlign: 'center', padding: '3rem' }}>{t('common.carregando')}</div>
                            ) : lancamentos.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
                                    {t('lanc.nenhumEncontrado')}
                                </div>
                            ) : (
                                <Table
                                    columns={columns}
                                    data={lancamentos}
                                    keyExtractor={(l) => l.id}
                                    sort={{ key: ordenacao.campo, direcao: ordenacao.direcao }}
                                    onSort={handleSort}
                                />
                            )}
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    className={styles.pageBtn}
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page <= 1}
                                >
                                    {t('common.anterior')}
                                </button>
                                <span className={styles.pageInfo}>
                                    {t('common.pagina')} {pagination.page} {t('common.de')} {pagination.totalPages}
                                </span>
                                <button
                                    className={styles.pageBtn}
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page >= pagination.totalPages}
                                >
                                    {t('common.proxima')}
                                </button>
                            </div>
                        )}
                    </Card>

                    <Modal
                        isOpen={obsModal !== null}
                        onClose={() => setObsModal(null)}
                        title={t('lanc.observacao')}
                    >
                        <p className={styles.obsModalText}>{obsModal}</p>
                    </Modal>

                    <Modal
                        isOpen={isModalOpen && editingLancamento !== null}
                        onClose={() => setIsModalOpen(false)}
                        title={t('lanc.editarLancamento')}
                    >
                        <form className={styles.form} onSubmit={handleSubmit}>
                            {editingLancamento && (
                                <p className={styles.lancadoPorDetalhe}>
                                    {t('lanc.lancadoPor')}: <strong>{editingLancamento.lancadoPor || '-'}</strong>
                                </p>
                            )}
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('lanc.tipo')} *</label>
                                    <SearchableSelect
                                        fullWidth
                                        value={formData.categoria}
                                        onChange={(v) => {
                                            handleInputChange('categoria', v);
                                            handleInputChange('subCategoria', '');
                                        }}
                                        required
                                        options={[
                                            { value: 'GASTOS', label: t('lanc.gasto') },
                                            { value: 'RECEITA', label: t('lanc.receita') },
                                        ]}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('lanc.data')} *</label>
                                    <input
                                        type="date"
                                        className={styles.input}
                                        value={formData.data}
                                        onChange={(e) => handleInputChange('data', e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('lanc.categoria')} *</label>
                                    <SearchableSelect
                                        fullWidth
                                        value={formData.subCategoria}
                                        onChange={(v) => handleInputChange('subCategoria', v)}
                                        required
                                        placeholder={t('common.selecione')}
                                        options={subCategoriasFull
                                            .filter((s: any) => s.categoria?.tipo === (formData.categoria === 'GASTOS' ? 'SAIDA' : 'ENTRADA'))
                                            .map((s: any) => ({ value: s.nome, label: t(s.nome) }))}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('receita.formaPagamento')} *</label>
                                    <SearchableSelect
                                        fullWidth
                                        value={formData.formaPagamento}
                                        onChange={(v) => handleInputChange('formaPagamento', v)}
                                        required
                                        placeholder={t('common.selecione')}
                                        options={formasPagamento.map((forma) => ({ value: forma, label: t(forma) }))}
                                    />
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>{t('lanc.valor')} *</label>
                                <div className={styles.inputWithPrefix}>
                                    <span className={styles.currencyPrefix}>{currencySymbol}</span>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={formData.valor === 0 ? '' : formatInputCurrency(Math.round(formData.valor * 100).toString())}
                                        onChange={handleValorChange}
                                        placeholder={prefs.language === 'en-US' ? '0.00' : '0,00'}
                                        required
                                    />
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>{t('receita.infoAdicionais')}</label>
                                <textarea
                                    className={styles.textarea}
                                    value={formData.informacoesAdicionais}
                                    onChange={(e) => handleInputChange('informacoesAdicionais', e.target.value)}
                                    placeholder={t('lanc.obsPlaceholder')}
                                />
                            </div>

                            <div className={styles.formActions}>
                                <Button variant="secondary" type="button" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
                                    {t('common.cancelar')}
                                </Button>
                                <Button type="submit" disabled={isSaving}>
                                    {isSaving ? t('common.salvando') : editingLancamento ? t('common.salvar') : t('lanc.cadastrar')}
                                </Button>
                            </div>
                        </form>
                    </Modal>
                </div>
            </main>
        </div>
    );
}